// ============================================================
// SkyPlus Feature Route Guard
// app/components/skyplus/shared/sector/feature.guard.ts
//
// Route guard that blocks navigation to feature-gated routes
// when the feature is not enabled for the current tenant.
//
// Usage in app.routes.ts:
//   {
//     path: 'intelligence',
//     component: AiChatComponent,
//     canActivate: [AuthGuard, FeatureGuard],
//     data: { feature: 'aiChat' }
//   }
//
// If sector.feature('aiChat') returns false, the guard blocks
// navigation and redirects to dashboard. This means even if a
// user manually types /intelligence in the URL bar of a client
// deployment where AI chat is off, they get bounced.
// ============================================================

import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot
} from '@angular/router';
import { SectorService } from './sector.service';
import { FeatureFlags } from './sector.model';

@Injectable({ providedIn: 'root' })
export class FeatureGuard implements CanActivate {

  constructor(
    private sector: SectorService,
    private router: Router
  ) {}

  canActivate(route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): boolean {
    const feature = route.data?.['feature'] as keyof FeatureFlags | undefined;

    if (!feature) {
      return true;
    }

    if (this.sector.feature(feature)) {
      return true;
    }

    console.warn(`[FeatureGuard] Route blocked - feature '${feature}' not enabled for tenant.`);
    this.router.navigate(['/dashboard']);
    return false;
  }
}
